import { _ } from "../polyglot/polyglot.js";
import { eventManager } from "@foretagsplatsen/yaem";
import { getResult } from "../helpers/functional-util.js";
import { hasOwnProp, valueOrDefault } from "../helpers/object-util.js";
import { router, widget } from "@foretagsplatsen/widgetjs";
import applicationFrame from "./applicationFrame.js";
import notifier from "../widgets/notifier/notifier.js";
import session from "../session.js";
import theme from "../theme.js";
import translateServerNotification from "./translateServerNotification.js";

/**
 * `application` is the base common class of all applications.
 *
 * `initialize` is called upon page loading by
 * `app_main.js`. Routing should be setup in `initialize`.
 *
 * Use `addAction` to add register route actions to the router.
 */
function application(spec, my) {
	spec ||= {};
	my ||= {};

	const that = widget(spec, my);

	my.actions = [];
	my.currentUser = session.getCurrentUser();

	// Override to specify a document for 404.
	my.routeNotFoundDocument = undefined;

	// Array of routes that should not be displayed when marketing
	// pages is turned on.  When `undefined`, all routes are
	// displayed.
	my.routeRestrictedlist = undefined;

	that.getActions = function () {
		return my.actions;
	};

	that.getFrame = function () {
		return my.frame;
	};

	// TODO: Temporary: values from the server are old
	that.setTheme = function (options) {
		const agencyLogo =
			options.agencyLogoUrl ||
			(options.agencyLogo && options.agencyLogo.path
				? options.agencyLogo.path
				: "/Client/img/back-to-agency-icon-large@2x.png");
		const themeOptions = {
			colors: {
				"brand-primary": options.brandPrimary || "#0099cc",
				"brand-primary-dark": options.brandPrimaryDark || "#0187b3",
				"brand-primary-verydark":
					options.brandPrimaryVeryDark || "#01769d",
				"brand-navbar-font-color":
					options.brandNavbarFontColor || "#ffffff",
			},
			brandingName: options.brandingName || "V3",
			agencyLogo,
			agencyBrandingLogo:
				options.agencyBrandingLogoUrl ||
				(options.agencyBrandingLogo && options.agencyBrandingLogo.path
					? options.agencyBrandingLogo.path
					: agencyLogo),
			agencyPrintLogo:
				options.agencyPrintLogoUrl ||
				(options.agencyPrintLogo && options.agencyPrintLogo.path
					? options.agencyPrintLogo.path
					: agencyLogo),
			backToAgencyIcon: options.backToAgencyIcon,
			backToAgencyLabel: options.backToAgencyLabel,
			showSignOff: options.showSignOff, // show the FTGP logo
			advancedThemeColors: options.advancedThemeColors,
		};

		theme.setup(themeOptions);
	};

	that.remoteUrl = function () {};

	my.onSetDocument = eventManager.at("app").createEvent("documentSet");

	/**
	 * Helper that sets-up a route for a document action and
	 * a entry in the navigation widget.
	 *
	 * When a user asks for the index page (i.e., route=''), an action
	 * whose pattern is the empty string will be activated. If no such
	 * action exist, the action with the lowest strictly positive
	 * `indexPriority` number is activated.
	 *
	 * @param {string} options.pattern - Route pattern to match URLs against.
	 * @param {string} [options.doc] - Document to attach to action
	 * @param {function} [options.docFactory] - Factory function used to create documents instead of providing `options.doc`
	 * @param {string} [options.name] - Name of the route
	 * @param {string} [options.action] - Callback to execute on match. First argument to callback is doc
	 *                                thereafter parameters in same order as defined in pattern. (optional)
	 * @param {number} [options.indexPriority] - The smaller the number, the
	 * higher the chances a user has to arrive to the document if he
	 * asks for the index page. Use 0 to specify that the action
	 * should not be considered as a candidate index page.
	 */
	that.addAction = function (options) {
		const name = valueOrDefault(options.name, my.nextId());
		const pattern = valueOrDefault(options.pattern, name);

		my.actions.push(options);

		const route = my.router.addRoute({ pattern, name });

		my.router.routeMatched.register((result) => {
			if (route !== result.getRoute()) {
				return;
			}

			if (!my.isActionEnabled(options)) {
				my.displayNotFound();
				return;
			}

			my.beforeActionMatched(options, result);
			options.beforeRedirection?.();
			my.handleRouting(options, result);
		});
	};

	my.handleRouting = function (options, result) {
		my.handleActionRouting(options, result);
	};

	my.handleActionRouting = function (options, result) {
		let doc = options.doc;
		const route = result.getRoute();

		if (!doc && options.docFactory) {
			doc = my.makeDocument(options, route);
		}

		options.currentDoc = doc;

		my.setDocument(doc, my.wantsMarketingLayer(route.name));

		my.afterActionMatched(options, result);

		my.performRoutingAction(
			doc,
			options.action,
			result.getActionArguments(),
		);
	};

	my.makeDocument = function (options, route) {
		let doc;

		if (options.cachedDoc) {
			doc = my.cachedDocs[route] || options.docFactory();
			my.cachedDocs[route] = doc;
		} else {
			doc = options.docFactory();
		}

		doc.isCachedDocument = options.cachedDoc;

		return doc;
	};

	my.performRoutingAction = function (doc, action, actionArguments) {
		if (!action) {
			return;
		}
		action.apply(my.router, [doc].concat(actionArguments));
	};

	/**
	 * Adds an action for each key/value pair of
	 * `actionsMap`. Each key is used as the pattern of the
	 * action. The value should be a object with the action
	 * options as in `that.addAction`.
	 *
	 * @param {object} actionsMap - Object containing the action keys/values.
	 */
	that.addActions = function (actionsMap) {
		for (const key in actionsMap) {
			if (hasOwnProp(actionsMap, key)) {
				my.actions.push(actionsMap[key]);
				const routeOptions = Object.create(actionsMap[key]);
				routeOptions.pattern = key;
				that.addAction(routeOptions);
			}
		}
	};

	that.enableMarketingPages = function ({
		routesWithPotentialMarketingOverlay,
	}) {
		my.routesWithPotentialMarketingOverlay =
			routesWithPotentialMarketingOverlay;
	};

	my.frame = applicationFrame();

	// Cached documents that we keep for reuse.
	my.cachedDocs = {};

	my.router = router.getRouter();

	my.setDocument = function (newDocument, hasMarketingLayer = false) {
		const previousDocument = my.frame.getDocument();
		if (previousDocument) {
			previousDocument.willDetach();
		}
		if (previousDocument && !previousDocument.isCachedDocument) {
			previousDocument.dispose();
		}
		my.frame.setDocument(newDocument, hasMarketingLayer);
		my.onSetDocument.trigger(newDocument);
	};

	my.initializeApplication = function () {
		my.router.on("routeNotFound", my.handleRouteNotFound);
		// Render ourself on BODY
		that.appendTo("body");
		my.router.start();

		my.displayServerSideNotifications();
	};

	my.handleRouteNotFound = function (url) {
		if (url.length === 0) {
			my.handleIndexPageRequest();
		} else {
			my.displayNotFound();
		}
	};

	my.handleIndexPageRequest = function () {
		const indexActions = my.actions.filter(
			(action) => action.indexPriority > 0,
		);

		// sort actions by increasing indexPriority so the first elements of
		// indexActions are the first candidates to try.
		indexActions.sort(
			(action1, action2) => action1.indexPriority - action2.indexPriority,
		);

		// pick the first action which is enabled
		const indexAction = indexActions.find((action) =>
			my.isActionEnabled(action),
		);

		if (indexAction) {
			my.redirectTo(indexAction.name);
		} else {
			my.displayNotFound();
		}
	};

	my.isActionEnabled = function ({ enabled, roles }) {
		if (enabled && !getResult(enabled)) {
			return false;
		}

		const companyId =
			session.getCompany() && session.getCompany().getLegacyId();
		const currentUser = session.getCurrentUser();

		return !companyId || currentUser.hasCompanyRole(roles, companyId);
	};

	my.displayNotFound = function () {
		my.setDocument(my.routeNotFoundDocument);
	};

	/**
	 * Takes conditions as arguments.
	 *
	 * Each rule can be a boolean, predicate or feature -- as defined in
	 * featureToggle.js.
	 */
	my.buildRule = function (...args) {
		return function () {
			return args.every((condition) => {
				if (condition.isFeature) {
					return condition.asRoutingRule();
				}
				return getResult(condition);
			});
		};
	};

	my.redirectToLogin = function () {
		const returnUrl = encodeURIComponent(window.location.pathname);
		my.redirectToLocationPath(`/External#!/login?ReturnUrl=${returnUrl}`);
	};

	my.beforeActionMatched = function () {};

	my.afterActionMatched = function () {};

	that.renderContentOn = function (html) {
		html.render(my.frame);
	};

	that.initialize = function () {
		my.initializeApplication();
	};

	// Printing - currently we only support chrome
	// look at r3347 for Görans attempt to add support for IE/Firefox/..
	// if (jQuery.browser.webkit && window.matchMedia) {
	//     var mediaQueryList = window.matchMedia('print');
	//     mediaQueryList.addListener(function (mql) {
	//     // TODO: this cannot work. Events is not a global
	//     // anymore. We should keep support for printing
	//     // though.
	//     if (mql.matches) {
	//        events.at('app').trigger('beforePrint');
	//     }
	//     else {
	//        events.at('app').trigger('afterPrint');
	//     }
	//     });
	// }

	my.displayServerSideNotifications = function () {
		if (session.notifications.infos) {
			session.notifications.infos.forEach((message) => {
				notifier.success(translateServerNotification(message));
			});

			session.notifications.infos.length = 0;
		}

		if (session.notifications.warnings) {
			session.notifications.warnings.forEach((message) => {
				notifier.warning(translateServerNotification(message));
			});

			session.notifications.warnings.length = 0;
		}

		if (session.notifications.errors) {
			session.notifications.errors.forEach((message) => {
				notifier.error(translateServerNotification(message));
			});

			session.notifications.errors.length = 0;
		}
	};

	my.logoutNavigationItems = function () {
		return [my.backToPortalNavigationItem(), my.logoutNavigationItem()];
	};

	my.backToPortalNavigationItem = function () {
		const homepageUrl = (session.agency && session.agency.homepage) || "";

		return {
			linkToUrl: homepageUrl,
			label: theme.backToAgencyLabel || _("Back to portal"),
			cssClass: "menu-user-logout-sm",
			dataTestName: "nav-back-to-portal",
			visible: my.shouldDisplayBackToPortalMenuItem,
		};
	};

	my.logoutNavigationItem = function () {
		return {
			dataTestName: "nav-logout",
			linkToUrl: my.createLogoutLink(),
			label: (html) => my.renderLogoutLabel(html),
			cssClass: "menu-user-logout-sm",
		};
	};

	my.renderLogoutLabel = function (html) {
		if (session.agency && session.agency.shouldRedirectToAgencyOnLogout) {
			html.render(theme.backToAgencyLabel || _("Back to portal"));
		} else {
			html.render(_("Logout"));
		}
	};

	my.createLogoutLink = function () {
		if (
			session.agency &&
			session.agency.isProvisionedByOneClick &&
			session.agency.homepage
		) {
			return session.agency.homepage;
		}
		return "/External/Authenticate/Logout";
	};

	my.wantsMarketingLayer = function (routeName) {
		const routesWithPotentialMarketingOverlay =
			my.routesWithPotentialMarketingOverlay;

		return (
			routesWithPotentialMarketingOverlay &&
			routesWithPotentialMarketingOverlay.includes(routeName)
		);
	};

	my.shouldDisplayBackToPortalMenuItem = function () {
		const agency = session.agency;
		return agency && agency.isAgencyPortalLinkEnabled && agency.homepage;
	};

	return that;
}

export default application;
